import { editorCharClassOf } from '@messenger/lotus'
import { inkOf } from '@/utils/tinyMCE/updateDynamicText/namePartSeparators'

/** Elements whose content never paints as text in a TinyMCE box. */
const NON_TEXT_ELEMENTS =
  'script, style, template, noscript, iframe, object, embed, link, meta, title, head, textarea, select'

/**
 * An element's own inline declarations: property names lowercased, the
 * `!important` flag (`! important` too) dropped as the priority it is, empty
 * values skipped, the last declaration of a property winning, as the browser
 * reads them.
 */
export const inlineDeclarations = (el: Element): Map<string, string> => {
  const declarations = new Map<string, string>()
  for (const declaration of (el.getAttribute('style') ?? '').split(';')) {
    const colon = declaration.indexOf(':')
    if (colon < 0) continue
    const property = declaration.slice(0, colon).trim().toLowerCase()
    // Trimmed first, so the pattern starts at its literal `!` and reads a
    // value of any length in one pass.
    const value = declaration
      .slice(colon + 1)
      .trim()
      .replace(/!\s*important$/i, '')
      .trim()
    if (property !== '' && value !== '') declarations.set(property, value)
  }
  return declarations
}

/** The markers of a live TinyMCE field: a case detail, its table, a template. */
const LIVE_FIELD_SELECTOR =
  '[data-case-detail], [data-case-detail-table], [data-template]'

export const isHiddenElement = (el: Element): boolean =>
  inlineDeclarations(el).get('display')?.toLowerCase() === 'none'

/**
 * A hidden field TinyMCE shows again by itself: one it hid because the case
 * had no value for it, which reappears once the case has one. A name part
 * the person hid (`data-user-hidden`) stays hidden.
 */
export const isAutoHiddenField = (el: Element): boolean =>
  isHiddenElement(el) &&
  el.matches(LIVE_FIELD_SELECTOR) &&
  el.getAttribute('data-user-hidden') !== 'true'

/**
 * Whether TinyMCE, showing a field it hid for want of a value, puts a space
 * between the field and its sibling on one side, as its dynamic-text update
 * does: the nearest sibling that paints is content with no whitespace at the
 * edge facing the field. Zero-width text and another hidden field are passed
 * over; whitespace text or a span of spaces is a separator already.
 */
export const autoHiddenFieldSpaced = (
  el: Element,
  side: 'before' | 'after',
): boolean => {
  const step = side === 'before' ? 'previousSibling' : 'nextSibling'
  for (let node = el[step]; node !== null; node = node[step]) {
    if (node.nodeType === Node.TEXT_NODE) {
      const ink = inkOf(node.nodeValue ?? '')
      if (ink === '') continue
      return !/\s/.test(side === 'before' ? ink[ink.length - 1] : ink[0])
    }
    if (node.nodeType !== Node.ELEMENT_NODE) continue
    const sibling = node as Element
    if (isHiddenElement(sibling)) continue
    const text = sibling.textContent ?? ''
    return (
      sibling.hasAttribute('data-case-detail') ||
      text === '' ||
      text.trim() !== ''
    )
  }
  return false
}

/**
 * The stored HTML of a TinyMCE item as a document body, with what never
 * paints taken out: a `display:none` subtree (a name part the person hid,
 * hidden spacing), except a field TinyMCE hid for want of a value; TinyMCE's
 * bogus nodes (a `data-mce-bogus` wrapper keeps its content, `all` takes
 * it); and script, style and template content. The markup is only parsed,
 * never rendered, so nothing in it runs.
 */
export const parseTinyMceHtml = (html: string): HTMLElement => {
  const body = new DOMParser().parseFromString(html, 'text/html').body
  body.querySelectorAll(NON_TEXT_ELEMENTS).forEach((el) => el.remove())
  body.querySelectorAll('[data-mce-bogus]').forEach((el) => {
    if (el.getAttribute('data-mce-bogus') === 'all') el.remove()
    else el.replaceWith(...el.childNodes)
  })
  body.querySelectorAll('[style]').forEach((el) => {
    if (isHiddenElement(el) && !isAutoHiddenField(el)) el.remove()
  })
  return body
}

/**
 * A text node's characters as a run holds them: the engine's artifact and
 * forbidden characters (TinyMCE's hair-space glue among them) stripped, a
 * source line break read as the space TinyMCE painted it as, and each run of
 * spaces and tabs made one. The model canon folds what is left.
 */
export const tinyMceText = (raw: string): string => {
  let text = ''
  for (const ch of raw) {
    const cls = editorCharClassOf(ch.codePointAt(0) ?? 0)
    if (cls === 'artifact' || cls === 'forbidden') continue
    text += cls === 'line-structure' ? ' ' : ch
  }
  return text.replace(/[ \t]+/g, ' ')
}

/**
 * Whether stored text holds a line open even when it converts to nothing: a
 * character CSS does not collapse (a no-break space, a hair space, a
 * zero-width space) paints a line box where plain spaces paint none.
 */
export const holdsLineOpen = (raw: string): boolean => /[^ \t\n\r\f]/.test(raw)
